import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Check, MessageCircle, Phone, ArrowLeft, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { API_BASE } from '@/lib/api';
import { toast } from 'sonner';
import { useSiteSettings, buildWhatsAppUrl } from '@/hooks/useSiteSettings';
import { openWhatsAppWithTracking } from '@/lib/gtag';
import axios from 'axios';

export default function Estimate() {
    // Flow: 'choose' → 'upload' (design + phone) → 'done'
    // OR:   'choose' → WhatsApp deep-link (leaves the page)
    const [step, setStep] = useState('choose');
    const [file, setFile] = useState(null);
    const [phone, setPhone] = useState('');
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [busy, setBusy] = useState(false);
    const inputRef = useRef();
    const s = useSiteSettings();
    const nav = useNavigate();

    const onFileChange = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 20 * 1024 * 1024) { toast.error('Max file size 20MB'); return; }
        setFile(f);
    };

    const openWhatsAppDirect = () => {
        const msg = 'Hi Kanbr, I want an estimate for my project. I will share the design here on WhatsApp.';
        const url = buildWhatsAppUrl(s.whatsapp_number, msg);
        openWhatsAppWithTracking(url, { source: 'estimate-page-cta', message: msg });
    };

    const submitDesign = async () => {
        if (!file) { toast.error('Please upload your design document'); return; }
        if (!/^\d{10}$/.test(phone)) { toast.error('Please enter a valid 10-digit phone number'); return; }
        setBusy(true);
        try {
            // Send file + contact fields in a single request so the phone
            // arrives in the design email itself (not a separate lead email).
            const fd = new FormData();
            fd.append('file', file);
            fd.append('phone', phone);
            if (name) fd.append('name', name);
            if (email) fd.append('email', email);
            if (message) fd.append('message', message);
            await axios.post(`${API_BASE}/uploads/design`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            setStep('done');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not share your design. Please try again.');
        } finally { setBusy(false); }
    };

    return (
        <div className="kanbr-container py-10 max-w-3xl">
            <div className="eyebrow mb-2">Get an estimate</div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900 leading-tight">Share your design.<br />We&apos;ll estimate the rest.</h1>
            <p className="text-slate-600 mt-3 text-sm md:text-base leading-relaxed">
                Two easy ways to get a quote. Upload your drawing directly, or start a WhatsApp chat with our team.
            </p>

            <div className="mt-8" data-testid="estimate-panel">
                {step === 'choose' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="estimate-choose-step">
                        <button
                            type="button"
                            onClick={() => setStep('upload')}
                            data-testid="estimate-choose-upload"
                            className="text-left border border-slate-200 bg-white hover:border-blue-900 hover:bg-blue-50 p-6 flex flex-col gap-3 transition-colors"
                        >
                            <div className="w-11 h-11 bg-blue-900 text-white flex items-center justify-center"><FileText className="w-5 h-5" /></div>
                            <div>
                                <div className="font-display font-semibold text-slate-900 text-base md:text-lg">Share your design</div>
                                <div className="text-xs text-slate-600 mt-1 leading-relaxed">
                                    Upload a PDF, drawing, cutlist or reference image. We&apos;ll review and revert with a detailed quote by WhatsApp or email.
                                </div>
                            </div>
                            <div className="mt-auto text-xs font-medium text-blue-900">Upload &amp; continue →</div>
                        </button>

                        <button
                            type="button"
                            onClick={openWhatsAppDirect}
                            data-testid="estimate-choose-whatsapp"
                            className="text-left border border-green-200 bg-green-50 hover:bg-green-100 p-6 flex flex-col gap-3 transition-colors"
                        >
                            <div className="w-11 h-11 bg-green-500 text-white flex items-center justify-center rounded-full">
                                <MessageCircle className="w-5 h-5" fill="currentColor" strokeWidth={0} />
                            </div>
                            <div>
                                <div className="font-display font-semibold text-green-900 text-base md:text-lg">Chat with us on WhatsApp</div>
                                <div className="text-xs text-green-800 mt-1 leading-relaxed">
                                    Prefer to talk? Start a WhatsApp conversation with our team on <strong>+91&nbsp;97737&nbsp;04406</strong>. Share your design or requirement right in the chat.
                                </div>
                            </div>
                            <div className="mt-auto text-xs font-medium text-green-800">Open WhatsApp →</div>
                        </button>
                    </div>
                )}

                {step === 'upload' && (
                    <div className="border border-slate-200 bg-white p-6 md:p-8 space-y-5">
                        <button
                            type="button"
                            onClick={() => setStep('choose')}
                            className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1"
                            data-testid="estimate-back-to-choose"
                        >
                            <ArrowLeft className="w-3 h-3" /> Back
                        </button>

                        <div className="text-lg font-display font-semibold text-slate-900">Share your design</div>

                        <label
                            htmlFor="doc"
                            className="border-2 border-dashed border-slate-300 hover:border-blue-900 bg-slate-50 hover:bg-blue-50 p-8 flex flex-col items-center justify-center gap-3 cursor-pointer transition-colors"
                            data-testid="estimate-dropzone"
                        >
                            <div className="w-12 h-12 bg-blue-900 text-white flex items-center justify-center"><Upload className="w-6 h-6" /></div>
                            <div className="text-sm font-semibold text-slate-900 text-center">{file ? file.name : 'Click to upload · PDF, JPG, PNG, DWG, DXF or DOC'}</div>
                            <div className="text-xs text-slate-500">Max 20MB · Your file is private and shared only with Kanbr team.</div>
                            <input ref={inputRef} id="doc" type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.dwg,.dxf" onChange={onFileChange} data-testid="estimate-file-input" />
                        </label>

                        <div>
                            <Label className="text-xs">Phone (10-digit) <span className="text-red-500">*</span></Label>
                            <Input
                                value={phone}
                                onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                                placeholder="9876543210"
                                className="mt-1 rounded-none h-10"
                                data-testid="estimate-phone"
                                required
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Label className="text-xs">Name (optional)</Label>
                                <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 rounded-none h-10" data-testid="estimate-name" />
                            </div>
                            <div>
                                <Label className="text-xs">Email (optional)</Label>
                                <Input value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 rounded-none h-10" data-testid="estimate-email" />
                            </div>
                        </div>

                        <div>
                            <Label className="text-xs">Requirement (optional)</Label>
                            <Textarea
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                rows={3}
                                className="mt-1 rounded-none"
                                placeholder="Describe your project — space, budget, timeline etc."
                                data-testid="estimate-notes"
                            />
                        </div>

                        <Button
                            onClick={submitDesign}
                            disabled={busy || !file || phone.length !== 10}
                            className="bg-blue-900 hover:bg-blue-950 rounded-none py-5 px-8"
                            data-testid="estimate-submit"
                        >
                            {busy ? 'Sharing…' : 'Share with Kanbr team'}
                        </Button>
                    </div>
                )}

                {step === 'done' && (
                    <div className="border border-green-200 bg-green-50 p-8 md:p-12 text-center" data-testid="estimate-done">
                        <div className="w-16 h-16 bg-green-600 text-white mx-auto flex items-center justify-center mb-4 rounded-full"><Check className="w-8 h-8" /></div>
                        <h2 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Your document has been shared with the Kanbr team.</h2>
                        <p className="text-slate-700 mt-3 max-w-xl mx-auto text-sm md:text-base leading-relaxed">
                            We&apos;ve received your design and phone number. Our team will review and get back to you shortly on WhatsApp or by phone.
                        </p>
                        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                            <Button
                                onClick={() => nav('/')}
                                className="bg-blue-900 hover:bg-blue-950 rounded-none py-5 px-8"
                                data-testid="estimate-back-home"
                            >
                                Go back to home
                            </Button>
                            <button
                                type="button"
                                onClick={openWhatsAppDirect}
                                className="border border-green-500 text-green-800 hover:bg-green-100 py-3 px-6 text-sm font-medium flex items-center gap-2"
                                data-testid="estimate-done-whatsapp"
                            >
                                <MessageCircle className="w-4 h-4" /> Or chat with us on WhatsApp
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
